'use client';

import {
  type AccountSettingsSection,
  removeAccountSettingsIntentFromHref,
  resolveAccountSettingsSection,
  resolveAccountSettingsSectionFromSearchParams,
} from '@/lib/account-settings/account-settings-route';
import {
  type ReactNode,
  createContext,
  createElement,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

export type CmsAccountSettingsSection = AccountSettingsSection;

export const resolveCmsAccountSettingsSection = resolveAccountSettingsSection;

export function useCmsAccountSettingsDialogState({
  consumeUrlIntent = false,
}: { consumeUrlIntent?: boolean } = {}) {
  const [activeSection, setActiveSection] = useState<CmsAccountSettingsSection>(
    resolveCmsAccountSettingsSection('credits')
  );
  const [open, setOpen] = useState(false);
  const [hasOpened, setHasOpened] = useState(false);

  useEffect(() => {
    if (!consumeUrlIntent) {
      return;
    }

    const requestedSection = resolveAccountSettingsSectionFromSearchParams(
      new URLSearchParams(window.location.search)
    );
    if (!requestedSection) {
      return;
    }

    setActiveSection(requestedSection);
    setHasOpened(true);
    setOpen(true);
  }, [consumeUrlIntent]);

  const onOpenChange = useCallback(
    (nextOpen: boolean) => {
      setOpen(nextOpen);
      if (!nextOpen && consumeUrlIntent) {
        window.history.replaceState(
          window.history.state,
          '',
          removeAccountSettingsIntentFromHref(window.location.href)
        );
      }
    },
    [consumeUrlIntent]
  );
  const onActiveSectionChange = useCallback(
    (section: CmsAccountSettingsSection) => {
      setActiveSection(resolveCmsAccountSettingsSection(section));
    },
    []
  );
  const openSettings = useCallback((section: CmsAccountSettingsSection) => {
    setActiveSection(resolveCmsAccountSettingsSection(section));
    setHasOpened(true);
    setOpen(true);
  }, []);

  return useMemo(
    () => ({
      activeSection,
      hasOpened,
      onActiveSectionChange,
      onOpenChange,
      open,
      openSettings,
    }),
    [
      activeSection,
      hasOpened,
      onActiveSectionChange,
      onOpenChange,
      open,
      openSettings,
    ]
  );
}

export type CmsAccountSettingsDialogState = ReturnType<
  typeof useCmsAccountSettingsDialogState
>;

const CmsAccountSettingsDialogContext =
  createContext<CmsAccountSettingsDialogState | null>(null);

export function CmsAccountSettingsDialogProvider({
  children,
}: {
  children: ReactNode;
}) {
  const state = useCmsAccountSettingsDialogState({ consumeUrlIntent: true });

  return createElement(
    CmsAccountSettingsDialogContext.Provider,
    { value: state },
    children
  );
}

export function useCmsAccountSettingsDialog() {
  const state = useContext(CmsAccountSettingsDialogContext);
  if (!state) {
    throw new Error(
      'CMS account settings must be rendered inside CmsAccountSettingsDialogProvider.'
    );
  }
  return state;
}
